import { camelize } from '@ember/string';
import { userEntryToNumber } from '@ebiz/pretty-num';
import FilterConstants from '../../filter-constants';

const VISITED_TABS_HEADER = 'v';
const VISITED_TABS = {
  'filter-type': 'f',
  specifications: 's',
  components: 'c',
  tolerances: 't',
  'next-steps': 'n',
};
const FILTER_HEADER = 'f';
const FILTERS = {
  lowPass: 'l',
  highPass: 'h',
  bandPass: 'b',
};
const SPECIFICATIONS_STRING = 's[{specifications}]';
const SPECIFICATIONS = {
  gain: 'g',
  gainUnit: 'u',
  passbandAttenuation: 'a',
  passbandFrequency: 'f',
  stopbandAttenuation: 'A',
  stopbandFrequency: 'F',
  filterResponseValue: 'r',
  filterResponseSlider: 'R',
  centerFrequency: 'c',
};
let GAIN_UNITS = {
  dB: 'd',
  'V/V': 'v',
};
const COMPONENTS_STRING = 'c[{components}]';
const COMPONENTS = {
  vsPlus: 'p',
  vsMinus: 'm',
  optimization: 'o',
  optimizationPreference: 'O',
  compensateForGbw: 'c',
  stages: 's',
  sortOrder: 'S',
  preferredAlgorithms: 'a',
  referenceVoltage: 'R',
};
const OPTIMIZATION = {
  Noise: 'n',
  VoltageRange: 'v',
  Power: 'p',
  RecommendedSpecificComponents: 'r',
  AllowedSpecificComponents: 'a',
};
const OPTIMIZATION_PREFERENCE = {
  nonSpecific: 'n',
  specific: 's',
};
const PREFERRED_ALGORITHMS = {
  // Convention is that small character is low pass, and capital letter is high pass
  // So low pass sallen key is s, and high pass sallen key is S
  // Band Pass Multiple Feedback is d (deliyannis friend) whild D for inductor
  'sallenKey|lowPass': 's',
};
const PREFERRED_ALGORITHMS_VALUES = {
  // This can be multiple characters as it is data rather than key and would work
  // with our parser.
  lowpass: 'L1',
  lowpassV2: 'L2',
};
const STAGES = {
  stageLetter: 'L',
  componentSizing: 's',
  gain: 'g',
  gainEnabled: 'G',
  implementation: 'i',
  opAmps: 'o',
};
const IMPLEMENTATION = {
  sallenKey: 's',
  multipleFeedback: 'm',
  inductor: 'i',
  deliyannisFriend: 'd',
  firstOrder: 'f',
};
const TOLERANCES_STRING = 't[{tolerances}]';
const TOLERANCES = {
  resistorTolerance: 'r',
  capacitorTolerance: 'c',
  inductorTolerance: 'l',
  opAmpGbwTolerance: 'g',
  resistorPreferredSeries: 'R',
  capacitorPreferredSeries: 'C',
  inductorPreferredSeries: 'L',
};
const TOLERANCE_VALUES = {
  '1%': 'O',
  '5%': 'F',
  '10%': 'T',
  '20%': 'W',
  ideal: 'i',
  '0.1%': 'o',
  '0.5%': 'f',
};

let isEmpty = function (val) {
  return val === undefined || val === null || val.length === 0;
};

export function dryVisitedTabs(tabs) {
  let result = VISITED_TABS_HEADER;
  for (let i = 0; i < tabs.length; i++) {
    if (VISITED_TABS[tabs[i]]) {
      result += VISITED_TABS[tabs[i]];
    }
  }
  return result;
}

export function dryFilter(type) {
  let result = FILTER_HEADER;
  if (FILTERS[type]) {
    result += FILTERS[type];
  }
  return result;
}

export function drySpecifications(specificationsInput) {
  let result = SPECIFICATIONS_STRING;
  let specificationsString = '';
  Object.keys(specificationsInput).forEach((key) => {
    if (specificationsString.length > 0 && !(isEmpty(SPECIFICATIONS[key]) || isEmpty(specificationsInput[key])))
      specificationsString += ',';
    switch (key) {
      case 'gainUnit': {
        specificationsString += SPECIFICATIONS['gainUnit'] + GAIN_UNITS[specificationsInput.gainUnit];
        break;
      }
      default: {
        if (!(isEmpty(SPECIFICATIONS[key]) || isEmpty(specificationsInput[key]))) {
          specificationsString += SPECIFICATIONS[key] + specificationsInput[key].toString();
        }
        break;
      }
    }
  });
  return result.replace('{specifications}', specificationsString);
}

export function dryComponents(componentsInput) {
  let result = COMPONENTS_STRING;
  let componentsString = '';
  Object.keys(componentsInput).forEach((key) => {
    if (componentsString.length > 0 && !(isEmpty(COMPONENTS[key]) || isEmpty(componentsInput[key])))
      componentsString += ',';
    switch (key) {
      case 'compensateForGbw': {
        componentsString += COMPONENTS['compensateForGbw'] + (componentsInput.compensateForGbw ? '1' : '0');
        break;
      }
      case 'optimization': {
        componentsString += COMPONENTS['optimization'] + OPTIMIZATION[componentsInput.optimization];
        break;
      }
      case 'optimizationPreference': {
        componentsString +=
          COMPONENTS['optimizationPreference'] + OPTIMIZATION_PREFERENCE[componentsInput.optimizationPreference];
        break;
      }
      case 'sortOrder': {
        let sortString = '';
        if (componentsInput.sortOrder.length > 0) {
          for (let i = 0; i < componentsInput.sortOrder.length; i++) {
            if (sortString.length > 0 && !isEmpty(componentsInput.sortOrder[i])) sortString += ',';
            sortString += COMPONENTS['sortOrder'] + componentsInput.sortOrder[i].toString();
          }
          componentsString += sortString;
        }
        break;
      }
      case 'stages': {
        let stagesList = '';
        if (componentsInput.stages.length > 0) {
          for (let i = 0; i < componentsInput.stages.length; i++) {
            if (stagesList.length > 0 && !isEmpty(componentsInput.stages[i])) stagesList += ',';
            stagesList += dryStages(COMPONENTS['stages'], componentsInput.stages[i]);
          }
          componentsString += stagesList;
        }
        break;
      }
      case 'preferredAlgorithms': {
        let preferredAlgorithmsString = '';
        if (componentsInput.preferredAlgorithms) {
          Object.keys(componentsInput.preferredAlgorithms).forEach((key) => {
            if (
              !(
                isEmpty(PREFERRED_ALGORITHMS[key]) ||
                isEmpty(componentsInput.preferredAlgorithms[key]) ||
                isEmpty(PREFERRED_ALGORITHMS_VALUES[componentsInput.preferredAlgorithms[key]])
              )
            ) {
              if (preferredAlgorithmsString.length > 0) preferredAlgorithmsString += ',';
              preferredAlgorithmsString +=
                PREFERRED_ALGORITHMS[key] +
                PREFERRED_ALGORITHMS_VALUES[componentsInput.preferredAlgorithms[key]].toString();
            }
          });
          if (preferredAlgorithmsString.length > 0)
            componentsString += `${COMPONENTS['preferredAlgorithms']}[${preferredAlgorithmsString}]`;
        }
        break;
      }
      case 'referenceVoltage': {
        componentsString += COMPONENTS['referenceVoltage'] + componentsInput.referenceVoltage;
        break;
      }
      default: {
        if (!(isEmpty(COMPONENTS[key]) || isEmpty(componentsInput[key]))) {
          componentsString += COMPONENTS[key] + componentsInput[key].toString();
        }
        break;
      }
    }
  });
  return result.replace('{components}', componentsString);
}

export function dryStages(stagesHeader, stageInput) {
  let stageString = '';
  Object.keys(stageInput).forEach((key) => {
    if (stageString.length > 0 && typeof stageInput[key] !== 'undefined' && stageInput[key] !== null)
      stageString += ',';
    switch (key) {
      case 'gainEnabled': {
        stageString += STAGES['gainEnabled'] + (stageInput.gainEnabled ? '1' : '0');
        break;
      }
      case 'implementation': {
        stageString += STAGES['implementation'] + IMPLEMENTATION[stageInput.implementation];
        break;
      }
      default: {
        if (!(isEmpty(STAGES[key]) || isEmpty(stageInput[key]))) {
          stageString += STAGES[key] + stageInput[key].toString();
        }
        break;
      }
    }
  });
  return stagesHeader + '[' + stageString + ']';
}

export function dryTolerances(toleranceInputs) {
  let result = TOLERANCES_STRING;
  let tolerancesString = '';
  Object.keys(toleranceInputs).forEach((key) => {
    if (tolerancesString.length > 0 && !(isEmpty(TOLERANCES[key]) || isEmpty(toleranceInputs[key])))
      tolerancesString += ',';
    switch (key) {
      case 'resistorTolerance':
      case 'capacitorTolerance':
      case 'inductorTolerance':
      case 'opAmpGbwTolerance': {
        tolerancesString += TOLERANCES[key] + TOLERANCE_VALUES[toleranceInputs[key]];
        break;
      }
      default: {
        if (!(isEmpty(TOLERANCES[key]) || isEmpty(toleranceInputs[key]))) {
          tolerancesString += TOLERANCES[key] + toleranceInputs[key].toString();
        }
        break;
      }
    }
  });
  return result.replace('{tolerances}', tolerancesString);
}

export function buildCircuitComponents(stages, circuits) {
  let components = {};
  for (let i = 0; i < stages.length; i++) {
    let stage = stages[i];
    let circuit = circuits[i];
    let stageImplementation = stage.implementation.replace(/ /g, '');
    let stageType = camelize(stage.filterName.substring(10).replace(/_/g, '-'));
    let stageLetter = stage.stageLetter;
    let stageObject = {};
    stageObject['type'] = stageType;
    stageObject['gain'] = userEntryToNumber(stage.gain.target);
    stageObject['fp'] = userEntryToNumber(stage.stageFp.target);
    stageObject['q'] = stage.stageQ.enabled ? userEntryToNumber(stage.stageQ.target) : 'N/A';
    switch (stageImplementation) {
      case FilterConstants.implementationType.gain: {
        stageObject['r3'] = circuit.r3.value;
        stageObject['r4'] = circuit.r4.value;
        break;
      }
      case FilterConstants.implementationType.firstOrder: {
        stageObject['r1'] = circuit.r1.value;
        if (circuit.r3.value !== FilterConstants.Limits.INFINITY) {
          stageObject['r3'] = circuit.r3.value;
        }
        if (circuit.r4.value > 0) {
          stageObject['r4'] = circuit.r4.value;
        }
        stageObject['c1'] = circuit.c1.value;
        break;
      }
      case FilterConstants.implementationType.sallenKey: {
        stageObject['r1'] = circuit.r1.value;
        stageObject['r2'] = circuit.r2.value;
        if (circuit.r3.value !== FilterConstants.Limits.INFINITY) {
          stageObject['r3'] = circuit.r3.value;
        }
        if (circuit.r4.value > 0) {
          stageObject['r4'] = circuit.r4.value;
        }
        stageObject['c1'] = circuit.c1.value;
        stageObject['c2'] = circuit.c2.value;
        break;
      }
      case FilterConstants.implementationType.inductor: {
        stageObject['r1'] = circuit.r1.value;
        if (circuit.r3.value !== FilterConstants.Limits.INFINITY) {
          stageObject['r3'] = circuit.r3.value;
        }
        if (circuit.r4.value > 0) {
          stageObject['r4'] = circuit.r4.value;
        }
        stageObject['c1'] = circuit.c1.value;
        stageObject['l1'] = circuit.l1.value;
        break;
      }
      case FilterConstants.implementationType.multipleFeedback: {
        if (stageType === FilterConstants.filterType.lowPass) {
          stageObject['r1'] = circuit.r1.value;
          stageObject['r2'] = circuit.r2.value;
          stageObject['r5'] = circuit.r5.value;
          stageObject['c1'] = circuit.c1.value;
          stageObject['c2'] = circuit.c2.value;
        } else {
          stageObject['r1'] = circuit.r1.value;
          stageObject['r2'] = circuit.r2.value;
          stageObject['c1'] = circuit.c1.value;
          stageObject['c2'] = circuit.c2.value;
          stageObject['c3'] = circuit.c5.value;
        }
        break;
      }
      case FilterConstants.implementationType.deliyannisFriend: {
        if (stageType === FilterConstants.filterType.bandPass) {
          stageObject['r1'] = circuit.r1.value;
          stageObject['r2'] = circuit.r2.value;
          if (circuit.r3.value > 0) {
            stageObject['r3'] = circuit.r3.value;
          }
          if (circuit.r4.value !== FilterConstants.Limits.INFINITY) {
            stageObject['r4'] = circuit.r4.value;
          }
          stageObject['r5'] = circuit.r5.value;
          stageObject['c1'] = circuit.c1.value;
          stageObject['c2'] = circuit.c2.value;
        } else if (stageType === FilterConstants.filterType.notch) {
          stageObject['r1'] = circuit.r1.value;
          stageObject['r2'] = circuit.r2.value;
          if (circuit.r3.value > 0) {
            stageObject['r3'] = circuit.r3.value;
          }
          if (circuit.r4.value !== FilterConstants.Limits.INFINITY) {
            stageObject['r4'] = circuit.r4.value;
          }
          stageObject['r5'] = circuit.r5.value;
          stageObject['r6'] = circuit.r5.value;
          stageObject['c1'] = circuit.c1.value;
          stageObject['c2'] = circuit.c2.value;
        }
        break;
      }
    }
    components[stageLetter] = stageObject;
  }
  return components;
}
